// AI-CONTEXT: See docs/features/dashboard-participation-ladder-growth-card.md
import deriveParticipationStep from './deriveParticipationStep'
import { LADDER_LABELS } from './participationOptions'

const nodeName = (step) => `Trede ${step} · ${LADDER_LABELS[step]}`

const EMPTY = { averageStepChange: 0, count: 0, nodes: [], links: [] }

/**
 * @typedef {object} LadderFlowNode
 * @property {string} name - "Trede N · <label>" for the Sankey renderer
 * @property {number} step - the participatieladder trede (1-5)
 * @property {'begin' | 'current'} side - which column the node sits in
 */

/**
 * @typedef {object} LadderFlowLink
 * @property {number} source - index into `nodes` of the begin node
 * @property {number} target - index into `nodes` of the current node
 * @property {number} value - number of personas making that begin→current move
 */

/**
 * Builds the Sankey model for participatieladder growth: each persona's begin
 * trede (t0) flows to its current trede (t1). Begin and current treden are
 * always distinct nodes (even for the same step) so a "stayed put" persona is a
 * forward self-loop, not a cycle.
 *
 * Counted personas need a filled current situation (`t1.mainActivity`) and a
 * derivable trede for both moments — unmeasured begins/currents are excluded
 * rather than read as trede 0.
 *
 * @param {Array<{Participation?: {t0?: object, t1?: object}}>} personas
 * @returns {{ averageStepChange: number, count: number, nodes: Array<LadderFlowNode>, links: Array<LadderFlowLink> }}
 */
const buildLadderGrowthFlow = (personas) => {
    if (!Array.isArray(personas)) return { ...EMPTY }

    let count = 0
    let stepChangeSum = 0
    // key `${from}->${to}` → count
    const flowCounts = new Map()
    const beginSteps = new Set()
    const currentSteps = new Set()

    personas.forEach((persona) => {
        const participation = persona?.Participation || {}
        const t1 = participation.t1 || {}
        if (!t1.mainActivity) return

        const fromStep = deriveParticipationStep(participation.t0 || {})
        const toStep = deriveParticipationStep(t1)
        if (fromStep === null || toStep === null) return

        count += 1
        stepChangeSum += toStep - fromStep
        beginSteps.add(fromStep)
        currentSteps.add(toStep)

        const key = `${fromStep}->${toStep}`
        flowCounts.set(key, (flowCounts.get(key) || 0) + 1)
    })

    if (count === 0) return { ...EMPTY }

    // Sort each column descending by trede so the render order (top→bottom)
    // puts the highest trede at the top and the lowest at the bottom — the
    // Sankey is drawn with iterations=0, which preserves this array order.
    const beginSorted = [...beginSteps].sort((a, b) => b - a)
    const currentSorted = [...currentSteps].sort((a, b) => b - a)

    const nodes = [
        ...beginSorted.map((step) => ({ name: nodeName(step), step, side: 'begin' })),
        ...currentSorted.map((step) => ({ name: nodeName(step), step, side: 'current' })),
    ]

    const beginIndex = new Map(beginSorted.map((step, i) => [step, i]))
    const currentIndex = new Map(currentSorted.map((step, i) => [step, beginSorted.length + i]))

    const links = [...flowCounts.entries()].map(([key, value]) => {
        const [from, to] = key.split('->').map(Number)
        return { source: beginIndex.get(from), target: currentIndex.get(to), value }
    })

    return {
        averageStepChange: stepChangeSum / count,
        count,
        nodes,
        links,
    }
}

export default buildLadderGrowthFlow
